import { createContext, useContext, useState } from 'react';

import { useGetPropertyFields } from '../hooks/queries/useGetPropertyFields';
import { useMultiEditMutation } from '../hooks/useMultiEditMutation';
import { markAiDraftStarted } from '../utils/aiDraftCacheUtils';
import { buildAiDraftSaveData } from '../utils/amenitiesUtils';

import type { ReactNode } from 'react';
import type { GetUserPropertyDetailsAmenitiesGroupWebDto } from '@zg-rentals/ts-rental-manager-api-client';

type AiDraftGeneration = {
  generateAiDraft: (notes: string) => void;
  /** True from the moment the draft is generated until it is saved onto the listing. */
  isSavingAiDraft: boolean;
};

type Props = {
  listingAlias: string;
  groups: Array<GetUserPropertyDetailsAmenitiesGroupWebDto>;
  children: ReactNode;
};

const AiDraftGenerationContext = createContext<AiDraftGeneration>({
  generateAiDraft: () => {},
  isSavingAiDraft: false,
});

export const useAiDraftGeneration = () => useContext(AiDraftGenerationContext);

/**
 * Owns the generate call for the whole wizard rather than the step that starts it. The AI step
 * unmounts the moment the landlord is moved on to the next step, and a mutation callback dies with
 * the component that registered it, so a callback living there wrote the draft only sometimes.
 */
export const AiDraftGenerationProvider = ({ listingAlias, groups, children }: Props) => {
  const { mutate: getPropertyFields } = useGetPropertyFields();
  const { mutate: saveDraftToListing } = useMultiEditMutation({
    meta: { context: { api: 'multi', details: 'AiDraftGeneration.saveAiDraft' } },
  });

  // Held from the request until the save settles, so the drafting screen covers the write as well as
  // the generate. Reading the flag off the save mutation alone would leave a gap between the two
  // calls where nothing is pending and the step would flash an empty "Your draft is ready".
  const [isSavingAiDraft, setIsSavingAiDraft] = useState(false);

  const generateAiDraft = (notes: string) => {
    markAiDraftStarted(listingAlias, Date.now(), notes);
    setIsSavingAiDraft(true);

    getPropertyFields(
      { postData: { listingAlias, text: notes } },
      {
        onSuccess: (response) =>
          saveDraftToListing(
            {
              postData: buildAiDraftSaveData({
                listingAlias,
                groups,
                aiAmenityFields: response.amenityFields,
                description: response.description?.value,
              }),
            },
            { onSettled: () => setIsSavingAiDraft(false) },
          ),
        onError: () => setIsSavingAiDraft(false),
      },
    );
  };

  return (
    <AiDraftGenerationContext.Provider value={{ generateAiDraft, isSavingAiDraft }}>
      {children}
    </AiDraftGenerationContext.Provider>
  );
};
